import React, { useState } from "react";
import { Box, Dialog, DialogContent, IconButton, Tooltip, Typography } from "@mui/material";
import { AccountTree, Close, OpenInFull, Print } from "@mui/icons-material";
import { colors } from "../theme/designSystem";
import { WorkflowStepKind } from "../services/workflowsService";

// Diagrama read-only do fluxo (SVG puro — sem biblioteca). Coluna vertical de etapas com as
// ligações adjacentes retas e as demais roteadas por canaletas à direita.
// Usado no editor da versão (rascunho em edição) e na consulta do Playbook.

/** Destino "encerra o processo": qualquer alvo fora do conjunto de etapas cai no nó Fim. */
export const DIAGRAM_END = "__end__";

/** Destino "continua em outro fluxo de trabalho": prefixo + id do fluxo. */
export const DIAGRAM_WORKFLOW_PREFIX = "workflow:";

export const diagramWorkflowTarget = (workflowId: string): string =>
  `${DIAGRAM_WORKFLOW_PREFIX}${workflowId}`;

/** Id do fluxo quando o alvo aponta para outro fluxo; null quando é etapa ou fim. */
export const diagramWorkflowId = (target: string): string | null =>
  target.startsWith(DIAGRAM_WORKFLOW_PREFIX)
    ? target.slice(DIAGRAM_WORKFLOW_PREFIX.length) || null
    : null;

export interface WorkflowDiagramOption {
  uid: string;
  label: string;
  optionKey?: string;
  target: string;
}

/** `uid` é a referência interna do nó (no editor não é o step_key, que muda enquanto se edita). */
export interface WorkflowDiagramStep {
  uid: string;
  kind: WorkflowStepKind;
  name: string;
  next: string;
  options: WorkflowDiagramOption[];
}

// Margem esquerda abriga as canaletas do lado esquerdo: as saídas de um nó se distribuem
// entre as pontas direita e esquerda (ideia do Hilton, 05/09) em vez de saírem todas pela direita.
const NODE_X = 170;
const NODE_W = 260;
const ACTIVITY_H = 46;
const DECISION_H = 62;
const NODE_GAP = 34;
const CENTER_X = NODE_X + NODE_W / 2;
const END_W = 92;
const END_H = 30;
const END_X = NODE_X + (NODE_W - END_W) / 2;
const WORKFLOW_W = 176;
const WORKFLOW_H = 32;
const WORKFLOW_X = NODE_X + (NODE_W - WORKFLOW_W) / 2;
const WORKFLOW_TOP_GAP = 18;
const WORKFLOW_GAP = 10;
// Canaletas laterais com 24px entre si: com 18px os rótulos de 9px se sobrepunham.
const LANES = [454, 478, 502];
const LEFT_LANES = [146, 122, 98];
const SVG_W = 660;
// Ramos que saem do MESMO nó pela canaleta saem em alturas diferentes (12px entre eles), senão
// as retas e os rótulos nascem no mesmo ponto e viram um borrão.
const EXIT_STEP = 12;

const cut = (value: string, max: number) => (value.length > max ? `${value.slice(0, max - 1)}…` : value);

/** Título da caixa em até duas linhas (quebra por palavra); só corta se nem duas linhas bastarem. */
const wrapText = (value: string, perLine: number): string[] => {
  const words = value.trim().split(/\s+/);
  const lines: string[] = [];
  let current = "";
  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word;
    if (candidate.length > perLine && current) {
      lines.push(current);
      current = word;
      if (lines.length === 2) break;
    } else {
      current = candidate;
    }
  }
  if (lines.length < 2 && current) lines.push(current);
  if (lines.length === 2) {
    const used = lines.join(" ").length;
    if (used < value.trim().length) lines[1] = cut(`${lines[1]} ${value.trim().slice(used).trim()}`, perLine);
  }
  return lines.map((l) => cut(l, perLine));
};

/**
 * Cor por tipo de etapa. O desenho antigo era todo ciano e obrigava a ler o texto de cada caixa
 * para saber o que era o quê; com a cor, tipo se reconhece de longe — é o que faz o diagrama de
 * papel do Instituto Ninho funcionar.
 */
const NODE_STYLE: Record<WorkflowStepKind, { fill: string; stroke: string }> = {
  activity: { fill: "#e8f6f8", stroke: colors.primary },
  decision: { fill: "#fff7e6", stroke: "#fd7e14" },
  milestone: { fill: "#e8f6ec", stroke: "#28a745" },
};

const LEGEND: { label: string; fill: string; stroke: string; dashed?: boolean; round?: boolean }[] = [
  { label: "Atividade", ...NODE_STYLE.activity },
  { label: "Decisão", ...NODE_STYLE.decision },
  { label: "Marco", ...NODE_STYLE.milestone, round: true },
  { label: "Fim", fill: "#e9ecef", stroke: "#adb5bd", round: true },
  { label: "Outro fluxo", fill: "#f8f9fa", stroke: colors.primary, dashed: true },
];

interface WorkflowDiagramProps {
  steps: WorkflowDiagramStep[];
  /** Nome de cada fluxo de destino por id, para rotular os nós "segue em outro fluxo". */
  workflowNames?: Record<string, string>;
  /** Encolhe o desenho para caber na largura disponível, em vez de rolar na horizontal. */
  fit?: boolean;
  showLegend?: boolean;
  /** Botão que abre o desenho em tela cheia, em tamanho real e com rolagem. */
  expandable?: boolean;
  /** Título mostrado na tela cheia (nome do fluxo). */
  title?: string;
  /** Abre a tela cheia já na montagem (usado quando o link veio de outro diagrama em tela cheia). */
  defaultExpanded?: boolean;
  /**
   * Monta o endereço do fluxo de destino. Recebe se o clique partiu da tela cheia, para que o
   * fluxo seguinte abra do mesmo jeito — quem está lendo em tela cheia quer continuar em tela cheia.
   */
  buildWorkflowUrl?: (workflowId: string, fullscreen: boolean) => string;
  /** Impressão a partir da tela cheia; sem ela o botão de imprimir não aparece lá. */
  onPrint?: () => void;
  emptyMessage?: string;
  emptyHint?: string;
}

const WorkflowDiagram: React.FC<WorkflowDiagramProps> = ({
  steps,
  workflowNames = {},
  fit = false,
  showLegend = false,
  expandable = false,
  title,
  defaultExpanded = false,
  buildWorkflowUrl,
  onPrint,
  emptyMessage = "Sem etapas para desenhar",
  emptyHint = "O diagrama aparece conforme você adiciona etapas.",
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const openWorkflow = (workflowId: string) => {
    if (!buildWorkflowUrl) return;
    window.open(buildWorkflowUrl(workflowId, expanded), "_blank", "noopener");
  };

  if (steps.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 4, color: colors.textSecondary }}>
        <AccountTree sx={{ fontSize: 40, color: "#ced4da", mb: 1 }} />
        <Typography variant="body2" sx={{ color: colors.textPrimary }}>
          {emptyMessage}
        </Typography>
        <Typography variant="caption">{emptyHint}</Typography>
      </Box>
    );
  }

  let cursorY = 14;
  const nodes = steps.map((step, index) => {
    const height = step.kind === "decision" ? DECISION_H : ACTIVITY_H;
    const node = { uid: step.uid, index, kind: step.kind, name: step.name, y: cursorY, h: height };
    cursorY += height + NODE_GAP;
    return node;
  });
  const endY = cursorY;
  const byUid = new Map(nodes.map((n) => [n.uid, n]));

  // Alvo válido é etapa deste desenho ou outro fluxo; o resto (inclusive DIAGRAM_END) cai no Fim.
  const resolveTarget = (target: string): string | null => {
    if (byUid.has(target)) return target;
    return diagramWorkflowId(target) ? target : null;
  };

  const edges: { fromUid: string; to: string | null; label: string | null }[] = [];
  steps.forEach((step) => {
    if (step.kind === "decision") {
      step.options.forEach((option) =>
        edges.push({
          fromUid: step.uid,
          to: resolveTarget(option.target),
          label: option.label || option.optionKey || "opção",
        })
      );
      return;
    }
    edges.push({ fromUid: step.uid, to: resolveTarget(step.next), label: null });
  });

  // Um nó terminal por fluxo de destino, na ordem em que aparece nas ligações.
  const workflowTargets: string[] = [];
  edges.forEach((edge) => {
    if (edge.to && !byUid.has(edge.to) && !workflowTargets.includes(edge.to)) {
      workflowTargets.push(edge.to);
    }
  });
  // O nó "Fim" é sintético e só faz sentido quando quem encerra não é um marco: um fluxo que
  // termina em "Final" já diz o que precisa, e desenhar os dois parece final duplicado.
  const endingSteps = edges.filter((edge) => edge.to === null).map((edge) => byUid.get(edge.fromUid));
  const showEndNode = endingSteps.some((step) => step && step.kind !== "milestone");

  const endBlockBottom = () => (showEndNode ? endY + END_H : endY);
  const workflowY = (index: number) =>
    endBlockBottom() + WORKFLOW_TOP_GAP + index * (WORKFLOW_H + WORKFLOW_GAP);
  const svgHeight =
    (workflowTargets.length > 0
      ? workflowY(workflowTargets.length - 1) + WORKFLOW_H
      : endBlockBottom()) + 16;

  /** O mesmo desenho serve o container (ajustado) e a tela cheia (tamanho real). */
  const renderSvg = (fitted: boolean) => {
    // O roteamento é por render: são duas chamadas (painel e tela cheia) desenhando o mesmo grafo,
    // e compartilhar este estado fazia a segunda achar todas as retas "já usadas" e mandar cada
    // ligação para as canaletas laterais.
    const straightUsed = new Set<string>();
    // Canaleta por trecho ocupado: uma ligação só pega uma canaleta nova quando o trecho vertical
    // dela cruza o de outra já desenhada ali. Antes era rodízio cego (i % 5) e o diagrama virava
    // um feixe de cinco retas paralelas do topo ao fim.
    const makePicker = (lanes: number[]) => {
      const spans: Array<Array<[number, number]>> = lanes.map(() => []);
      return (top: number, bottom: number): number => {
        const pad = 6;
        for (let i = 0; i < lanes.length; i++) {
          const busy = spans[i].some(([a, b]) => top - pad < b && bottom + pad > a);
          if (!busy) { spans[i].push([top, bottom]); return lanes[i]; }
        }
        let best = 0;
        spans.forEach((sp, i) => { if (sp.length < spans[best].length) best = i; });
        spans[best].push([top, bottom]);
        return lanes[best];
      };
    };
    const pickRightLane = makePicker(LANES);
    const pickLeftLane = makePicker(LEFT_LANES);
    // Quantas ligações de cada nó vão pela canaleta (todas menos a reta para o próximo, se houver)
    const laneOutBySource = new Map<string, number>();
    edges.forEach((edge) => {
      const from = byUid.get(edge.fromUid);
      if (!from) return;
      const to = edge.to && byUid.has(edge.to) ? byUid.get(edge.to)! : null;
      const straightCandidate = to !== null ? to.index === from.index + 1 : edge.to === null && from.index === nodes.length - 1;
      const current = laneOutBySource.get(from.uid) ?? 0;
      laneOutBySource.set(from.uid, current + (straightCandidate ? 0 : 1));
    });
    const exitIndexBySource = new Map<string, number>();

    return (
      <svg
        width={fitted ? "100%" : SVG_W}
        height={fitted ? undefined : svgHeight}
        viewBox={`0 0 ${SVG_W} ${svgHeight}`}
        preserveAspectRatio="xMidYMin meet"
        role="img"
        aria-label="Diagrama do fluxo"
        style={{ flexShrink: 0, maxWidth: "100%" }}
      >
        <defs>
          <marker id="wf-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto">
            <path d="M0,0 L10,5 L0,10 z" fill="#adb5bd" />
          </marker>
        </defs>

        {edges.map((edge, i) => {
          const from = byUid.get(edge.fromUid);
          if (!from) return null;
          // Marco que encerra não aponta para nada: ele é o fim.
          if (edge.to === null && !showEndNode) return null;
          const to = edge.to && byUid.has(edge.to) ? byUid.get(edge.to)! : null;
          const workflowIndex = !to && edge.to ? workflowTargets.indexOf(edge.to) : -1;
          const target =
            to !== null
              ? { y: to.y, h: to.h, right: NODE_X + NODE_W, left: NODE_X }
              : workflowIndex >= 0
              ? { y: workflowY(workflowIndex), h: WORKFLOW_H, right: WORKFLOW_X + WORKFLOW_W, left: WORKFLOW_X }
              : { y: endY, h: END_H, right: END_X + END_W, left: END_X };
          const toY = target.y;
          const toH = target.h;
          // Nó de outro fluxo fica abaixo do Fim: a ligação sempre vai pela canaleta, senão a reta
          // passaria por cima do Fim.
          const canBeStraight =
            to !== null
              ? to.index === from.index + 1
              : workflowIndex < 0 && from.index === nodes.length - 1;
          const straight = canBeStraight && !straightUsed.has(from.uid);
          if (straight) straightUsed.add(from.uid);

          if (straight) {
            const midY = (from.y + from.h + toY) / 2;
            return (
              <g key={i}>
                <path
                  d={`M ${CENTER_X} ${from.y + from.h} L ${CENTER_X} ${toY - 7}`}
                  stroke="#adb5bd"
                  strokeWidth={1.5}
                  fill="none"
                  markerEnd="url(#wf-arrow)"
                />
                {edge.label && (
                  <text x={CENTER_X + 6} y={midY + 3} fontSize={9} fill={colors.textSecondary}>
                    {cut(edge.label, 30)}
                  </text>
                )}
              </g>
            );
          }

          const exitIndex = exitIndexBySource.get(from.uid) ?? 0;
          exitIndexBySource.set(from.uid, exitIndex + 1);
          const laneOut = laneOutBySource.get(from.uid) ?? 1;
          const y1 = from.y + from.h / 2 + (exitIndex - (laneOut - 1) / 2) * EXIT_STEP;
          const y2 = toY + toH / 2;
          // Saídas alternam entre a ponta direita e a esquerda do nó
          const goLeft = exitIndex % 2 === 1;
          const lane = goLeft
            ? pickLeftLane(Math.min(y1, y2), Math.max(y1, y2))
            : pickRightLane(Math.min(y1, y2), Math.max(y1, y2));
          const path = goLeft
            ? `M ${NODE_X} ${y1} H ${lane} V ${y2} H ${target.left - 7}`
            : `M ${NODE_X + NODE_W} ${y1} H ${lane} V ${y2} H ${target.right + 7}`;
          return (
            <g key={i}>
              <path d={path} stroke="#adb5bd" strokeWidth={1.5} fill="none" markerEnd="url(#wf-arrow)" />
              {edge.label && (
                <text
                  x={goLeft ? NODE_X - 6 : lane + 4}
                  y={y1 - 3}
                  fontSize={9}
                  fill={colors.textSecondary}
                  textAnchor={goLeft ? "end" : "start"}
                  paintOrder="stroke"
                  stroke="#fff"
                  strokeWidth={3}
                >
                  {cut(edge.label, 36)}
                </text>
              )}
            </g>
          );
        })}

        {nodes.map((node) => {
          const label = `${node.index + 1}. ${node.name || "(sem nome)"}`;
          const textY = node.y + node.h / 2 + 4;
          if (node.kind === "decision") {
            const cy = node.y + node.h / 2;
            return (
              <g key={node.uid}>
                <polygon
                  points={`${CENTER_X},${node.y} ${NODE_X + NODE_W},${cy} ${CENTER_X},${node.y + node.h} ${NODE_X},${cy}`}
                  fill={NODE_STYLE.decision.fill}
                  stroke={NODE_STYLE.decision.stroke}
                  strokeWidth={1.5}
                />
                <text x={CENTER_X} y={textY} fontSize={11} textAnchor="middle" fill={colors.textPrimary}>
                  {wrapText(label, 24).map((line, li, arr) => (
                    <tspan key={li} x={CENTER_X} dy={li === 0 ? (arr.length > 1 ? -6 : 0) : 13}>{line}</tspan>
                  ))}
                  <title>{label}</title>
                </text>
              </g>
            );
          }
          return (
            <g key={node.uid}>
              <rect
                x={NODE_X}
                y={node.y}
                width={NODE_W}
                height={node.h}
                rx={node.kind === "milestone" ? 22 : 6}
                fill={(NODE_STYLE[node.kind] ?? NODE_STYLE.activity).fill}
                stroke={(NODE_STYLE[node.kind] ?? NODE_STYLE.activity).stroke}
                strokeWidth={1.5}
              />
              <text x={CENTER_X} y={textY} fontSize={11} textAnchor="middle" fill={colors.textPrimary}>
                {wrapText(label, 38).map((line, li, arr) => (
                  <tspan key={li} x={CENTER_X} dy={li === 0 ? (arr.length > 1 ? -6 : 0) : 13}>{line}</tspan>
                ))}
                <title>{label}</title>
              </text>
            </g>
          );
        })}

        {showEndNode && (
          <>
            <rect x={END_X} y={endY} width={END_W} height={END_H} rx={15} fill="#e9ecef" stroke="#adb5bd" strokeWidth={1.5} />
            <text x={CENTER_X} y={endY + END_H / 2 + 4} fontSize={11} textAnchor="middle" fill={colors.textSecondary}>
              Fim
            </text>
          </>
        )}

        {workflowTargets.map((target, index) => {
          const workflowId = diagramWorkflowId(target) ?? "";
          const name = workflowNames[workflowId];
          const y = workflowY(index);
          const clickable = Boolean(buildWorkflowUrl && workflowId);
          return (
            <g
              key={target}
              onClick={clickable ? () => openWorkflow(workflowId) : undefined}
              style={clickable ? { cursor: "pointer" } : undefined}
            >
              <rect
                x={WORKFLOW_X}
                y={y}
                width={WORKFLOW_W}
                height={WORKFLOW_H}
                rx={6}
                fill="#f8f9fa"
                stroke={colors.primary}
                strokeWidth={1.5}
                strokeDasharray="5 3"
              />
              <text
                x={CENTER_X}
                y={y + WORKFLOW_H / 2 + 4}
                fontSize={10}
                textAnchor="middle"
                fill={colors.textSecondary}
              >
                {`→ ${cut(name || "Fluxo indisponível", 24)}`}
                <title>
                  {name
                    ? clickable
                      ? `Segue no fluxo: ${name} (abre em outra aba)`
                      : `Segue no fluxo: ${name}`
                    : "Segue em outro fluxo (indisponível)"}
                </title>
              </text>
            </g>
          );
        })}
      </svg>
    );
  };

  // Imprime só o diagrama da tela cheia: a classe no body esconde a página de trás (o Dialog é um
  // portal irmão do #root) e deixa o Paper fluir no papel.
  const printDialog = () => {
    document.body.classList.add("wf-printing");
    const cleanup = () => { document.body.classList.remove("wf-printing"); window.removeEventListener("afterprint", cleanup); };
    window.addEventListener("afterprint", cleanup);
    window.print();
    setTimeout(cleanup, 2000);
  };

  const legendBox = (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1.25,
              flex: 1,
              minWidth: 0,
              color: colors.textSecondary,
              fontSize: "0.72rem",
            }}
          >
            {LEGEND.filter((item) => item.label !== "Fim" || showEndNode).map((item) => (
              <Box key={item.label} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Box
                  component="span"
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: item.round ? "6px" : "3px",
                    backgroundColor: item.fill,
                    border: `1.5px ${item.dashed ? "dashed" : "solid"} ${item.stroke}`,
                  }}
                />
                {item.label}
              </Box>
            ))}
          </Box>
  );

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 1 }}>
        {showLegend && legendBox}
        {expandable && (
          <Tooltip title="Abrir em tela cheia">
            <IconButton
              size="small"
              className="no-print"
              onClick={() => setExpanded(true)}
              sx={{ color: colors.textSecondary, ml: "auto" }}
            >
              <OpenInFull fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Box
        className="workflow-diagram-scroll"
        sx={{
          overflowX: fit ? "hidden" : "auto",
          pb: 1,
          // Centralizado quando sobra espaço; o scroll continua valendo quando falta.
          display: "flex",
          justifyContent: "center",
        }}
      >
        {renderSvg(fit)}
      </Box>

      <Dialog
        fullScreen
        open={expanded}
        onClose={() => setExpanded(false)}
        PaperProps={{ sx: { backgroundColor: "#fff" } }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 2,
            py: 1,
            borderBottom: "1px solid #e9ecef",
          }}
        >
          <Typography sx={{ fontWeight: 600, color: colors.textPrimary, flex: 1, minWidth: 0 }} noWrap>
            {title || "Diagrama do fluxo"}
          </Typography>
          <Tooltip title="Imprimir o diagrama">
            <IconButton onClick={onPrint ?? printDialog} className="no-print">
              <Print />
            </IconButton>
          </Tooltip>
          <IconButton onClick={() => setExpanded(false)} title="Fechar" className="no-print">
            <Close />
          </IconButton>
        </Box>
        {/* Tamanho real e rolagem nos dois eixos: em tela cheia o objetivo é ler, não caber. */}
        <DialogContent sx={{ p: 2, overflow: "auto" }}>
          <Box sx={{ width: "100%", maxWidth: 760, mx: "auto" }}>
            <Box sx={{ mb: 1.5 }}>{legendBox}</Box>
            {renderSvg(true)}
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default WorkflowDiagram;
